'use strict'

/* -----------------------------------------------------------------------------
 * dependencies
 * -------------------------------------------------------------------------- */

// 3rd party
import { Formik } from 'formik'
import moment from 'moment'
import React, { useCallback, useEffect, useState } from 'react'

// walrus
import Box from '@components/kit/box'
import { BoxLink } from '@components/kit/link'
import Pressable from '@components/kit/pressable'
import Text from '@components/kit/text'
import submitForm from '@lib/submit-form'

// lib
import DataTable, { DataTableColumn } from '@components/ui/data-table'
import SectionHeader from '@components/ui/section-header'
import { useFollowUpsStore } from '@store/FollowUps'
import {
  FollowUpsSortOptions,
  IFollowUpDashboardItem
} from '@store/FollowUps/types'
import {
  DashboardFilterOption,
  toFilterOptions
} from './dashboard-filter-options'
import { ReportsSortButton } from './dashboard-sort-button.web'
import ReportsFilterSelect from './reports-filter-select.web'

/* -----------------------------------------------------------------------------
 * types
 * -------------------------------------------------------------------------- */

export type FollowUpDashboardFormValues = {
  company_ids?: number[]
  order_by?: FollowUpsSortOptions
  professional_ids?: number[]
}

const PAGE_SIZE = 50

const formatDate = (value?: string | null) =>
  value ? moment(value).utc().format('MM/DD/YYYY') : '—'

/* -----------------------------------------------------------------------------
 * columns
 * -------------------------------------------------------------------------- */

const columns: DataTableColumn<IFollowUpDashboardItem>[] = [
  {
    key: 'user_id',
    label: 'Member',
    render: row => (
      <BoxLink path={`/chat/${row.user_id}`}>
        <Text font='pro.text.13.medium' textColor='proAccent'>
          {row.user_id}
        </Text>
      </BoxLink>
    ),
    width: 110
  },
  {
    key: 'scheduled_date',
    label: 'Follow up date',
    render: row => (
      <Text font='pro.text.13.medium' textColor='proInk'>
        {formatDate(row.scheduled_date)}
      </Text>
    ),
    width: 160
  },
  {
    key: 'last_visit_date',
    label: 'Last visit',
    render: row => (
      <Text font='pro.text.13.regular' textColor='proTextMuted'>
        {formatDate(row.last_visit_date)}
      </Text>
    ),
    width: 150
  },
  {
    flex: 1,
    key: 'professional_name',
    label: 'PharmD',
    minWidth: 160,
    render: row => (
      <Text font='pro.text.13.regular' textColor='proTextMuted'>
        {row.professional_name || '—'}
      </Text>
    )
  },
  {
    flex: 1,
    key: 'company_name',
    label: 'Employer',
    minWidth: 180,
    render: row => (
      <Text font='pro.text.13.regular' textColor='proTextBody'>
        {row.company_name || '—'}
      </Text>
    )
  }
]

/* -----------------------------------------------------------------------------
 * MemberFollowUpDashboard
 * -------------------------------------------------------------------------- */

export default function MemberFollowUpDashboard() {
  const { getFollowUpDashboard } = useFollowUpsStore.getState()

  const [filters, setFilters] = useState<FollowUpDashboardFormValues>({
    company_ids: [],
    professional_ids: [],
    order_by: 'SCHEDULED_DATE_ASC'
  })
  const [results, setResults] = useState<IFollowUpDashboardItem[]>([])
  const [totalResults, setTotalResults] = useState<number>(0)
  const [professionals, setProfessionals] = useState<DashboardFilterOption[]>(
    []
  )
  const [companies, setCompanies] = useState<DashboardFilterOption[]>([])
  const [page, setPage] = useState<number>(0)
  const [hasNextPage, setHasNextPage] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isFetchingNextPage, setIsFetchingNextPage] = useState<boolean>(false)

  const { company_ids, professional_ids, order_by } = filters
  const hasFilters = company_ids.length > 0 || professional_ids.length > 0

  const fetchDashboard = useCallback(async () => {
    setIsLoading(true)
    const response = await getFollowUpDashboard({
      page: 0,
      page_size: PAGE_SIZE,
      company_ids,
      professional_ids,
      order_by
    })
    setResults(response?.results ?? [])
    setTotalResults(response?.total_events ?? 0)
    setPage(response?.page ?? 0)
    setHasNextPage(Boolean(response?.has_next_page))
    // filter options come from the unfiltered rows, so narrowing the dashboard
    // does not narrow the dropdowns along with it
    if (!hasFilters) {
      setProfessionals(
        toFilterOptions(
          response?.results ?? [],
          item => item.professional_id,
          item => item.professional_name
        )
      )
      setCompanies(
        toFilterOptions(
          response?.results ?? [],
          item => item.company_id,
          item => item.company_name
        )
      )
    }
    setIsLoading(false)
  }, [
    getFollowUpDashboard,
    company_ids,
    professional_ids,
    order_by,
    hasFilters
  ])

  useEffect(() => {
    fetchDashboard()
  }, [fetchDashboard])

  const loadNextPage = async () => {
    if (!hasNextPage || isFetchingNextPage) {
      return
    }
    setIsFetchingNextPage(true)
    const response = await getFollowUpDashboard({
      page: page + 1,
      page_size: PAGE_SIZE,
      company_ids,
      professional_ids,
      order_by
    })
    setResults(current => [...current, ...(response?.results ?? [])])
    setPage(response?.page ?? page + 1)
    setHasNextPage(Boolean(response?.has_next_page))
    setIsFetchingNextPage(false)
  }

  const handleFollowUpDashboardFormSubmit = submitForm(
    async (values: FollowUpDashboardFormValues) => {
      setFilters({
        company_ids: values.company_ids ?? [],
        professional_ids: values.professional_ids ?? [],
        order_by: values.order_by ?? 'SCHEDULED_DATE_ASC'
      })
    }
  )

  return (
    <Box mb='40'>
      <SectionHeader
        count={totalResults}
        description='Scheduled follow-ups, filtered by pharmacist and employer'
        title='Members with a scheduled follow-up'
      />

      <Formik<FollowUpDashboardFormValues>
        initialValues={{
          company_ids: [],
          order_by: 'SCHEDULED_DATE_ASC',
          professional_ids: []
        }}
        onSubmit={handleFollowUpDashboardFormSubmit}
      >
        <>
          {/* the endpoint does not return the ids yet, so each filter stays
              hidden until the rows carry the one its query parameter needs */}
          <Box
            alignItems='flex-start'
            flexDirection='row'
            flexWrap='wrap'
            gap='12'
            mb='12'
          >
            <Box width={220}>
              <ReportsSortButton
                ascOption='SCHEDULED_DATE_ASC'
                descOption='SCHEDULED_DATE_DESC'
                label='FOLLOW UP DATE'
              />
            </Box>
            {professionals.length > 0 ? (
              <Box width={220}>
                <ReportsFilterSelect
                  filterOptions={professionals}
                  idKey='id'
                  label='PHARMD'
                  name='professional_ids'
                />
              </Box>
            ) : null}
            {companies.length > 0 ? (
              <Box width={220}>
                <ReportsFilterSelect
                  filterOptions={companies}
                  idKey='id'
                  label='EMPLOYER'
                  name='company_ids'
                />
              </Box>
            ) : null}
          </Box>

          <DataTable<IFollowUpDashboardItem>
            columns={columns}
            data={results}
            emptyDescription='No members have a follow-up scheduled right now.'
            emptyTitle='Nothing scheduled'
            footer={
              hasNextPage ? (
                <Box
                  alignItems='center'
                  borderColor='proDividerSoft'
                  borderStyle='solid'
                  borderTopWidth={1}
                  py='12'
                >
                  <Pressable
                    disabled={isFetchingNextPage}
                    onPress={loadNextPage}
                  >
                    <Text font='pro.text.12.semibold' textColor='proAccent'>
                      {isFetchingNextPage ? 'Loading…' : 'Load more'}
                    </Text>
                  </Pressable>
                </Box>
              ) : null
            }
            getRowKey={row => String(row.user_id)}
            isLoading={isLoading}
          />
        </>
      </Formik>
    </Box>
  )
}
